import React, { useState, useEffect, useMemo } from 'react';
import { Modal, Typography, Button, App, Spin } from 'antd';
import { PrinterOutlined } from '@ant-design/icons';
import { useCurrency } from '../../hooks/useCurrency';
import { useResponsive } from '../../hooks/useResponsive';
import { useRestaurantTimeZone } from '../../hooks/useTimeZone';
import { CashRegisterDto } from '../../types/cashRegister';
import { printAgentService } from '../../services/printAgentService';
import { bluetoothPrintService } from '../../services/bluetoothPrintService';
import { useGetRestaurantSettingsQuery } from '../../services/restaurantsApi';
import { useGetBoolSettingQuery } from '../../services/settingsApi';
import { useCloudPrint } from '../../hooks/useCloudPrint';
import { useGetOrdersQuery } from '../../services/ordersApi';
import dayjs from 'dayjs';

const { Text } = Typography;

interface CashCountModalProps {
  visible: boolean;
  expectedAmount: number;
  cashRegister?: CashRegisterDto | null;
  onCancel: () => void;
  onConfirm: () => void;
}

interface DaySummary {
  // Ingresos por tipo de pago
  cashSales: number;
  cardSales: number;
  transferSales: number;
  otherSales: number;
  totalSales: number;

  // Propinas y descuentos
  tips: number;
  totalDiscounts: number;

  // Movimientos de caja
  initialAmount: number;
  expenses: number;
  drops: number;
  payouts: number;
  corrections: number;

  // Ordenes abiertas (en mesa)
  openOrdersCount: number;
  openOrdersTotal: number;

  // Personas
  totalAdults: number;
  totalChildren: number;
  totalGuests: number;
  currentGuests: number; // Personas actualmente en el restaurante

  // Totales
  expectedCash: number;
  totalRevenue: number;
}

const CashCountModal: React.FC<CashCountModalProps> = ({
  visible,
  expectedAmount,
  cashRegister,
  onCancel,
  onConfirm,
}) => {
  const { message } = App.useApp();
  const { currencySymbol } = useCurrency();
  const { isMobile } = useResponsive();
  const { formatInTimeZone } = useRestaurantTimeZone();
  const { data: restaurantSettings } = useGetRestaurantSettingsQuery();
  const [printAgentAvailable, setPrintAgentAvailable] = useState(false);
  const [bluetoothAvailable, setBluetoothAvailable] = useState(false);
  const [printing, setPrinting] = useState(false);

  // Cloud print
  const { data: cloudPrintSetting } = useGetBoolSettingQuery('CloudPrintEnabled');
  const cloudPrintEnabled = cloudPrintSetting?.value ?? false;
  const { printJob: cloudPrintJob } = useCloudPrint();

  // Fetch active orders to calculate open orders totals and current guests
  const { data: ordersData, isLoading: ordersLoading } = useGetOrdersQuery(
    { activeOnly: true },
    { skip: !visible }
  );

  // Check for available print services when modal opens
  useEffect(() => {
    const checkPrintServices = async () => {
      setBluetoothAvailable(bluetoothPrintService.isAvailable());
      setPrintAgentAvailable(await printAgentService.isAvailable());
    };
    if (visible) {
      checkPrintServices();
    }
  }, [visible]);

  // Calculate day summary from cash register and orders
  const daySummary: DaySummary = useMemo(() => {
    const summary: DaySummary = {
      cashSales: cashRegister?.summary?.totalSales ?? 0,
      cardSales: cashRegister?.summary?.totalCardSales ?? 0,
      transferSales: cashRegister?.summary?.totalTransferSales ?? 0,
      otherSales: cashRegister?.summary?.totalOtherSales ?? 0,
      totalSales: cashRegister?.summary?.totalAllSales ?? 0,
      tips: cashRegister?.summary?.totalTips ?? 0,
      // Discounts on orders that generated a movement in this register (computed
      // backend-side). Fully-comped orders never hit the register, so they're excluded.
      totalDiscounts: cashRegister?.summary?.totalDiscounts ?? 0,
      initialAmount: cashRegister?.summary?.initialAmount ?? cashRegister?.initialAmount ?? 0,
      expenses: cashRegister?.summary?.totalExpenses ?? 0,
      drops: cashRegister?.summary?.totalDrops ?? 0,
      payouts: cashRegister?.summary?.totalPayouts ?? 0,
      corrections: cashRegister?.summary?.totalCorrections ?? 0,
      openOrdersCount: 0,
      openOrdersTotal: 0,
      // Comensales cobrados en esta caja: los calcula el backend, que sí ve las
      // órdenes ya cerradas. Aquí solo tenemos las abiertas.
      totalAdults: cashRegister?.summary?.totalAdults ?? 0,
      totalChildren: cashRegister?.summary?.totalChildren ?? 0,
      totalGuests: (cashRegister?.summary?.totalAdults ?? 0) + (cashRegister?.summary?.totalChildren ?? 0),
      currentGuests: 0,
      expectedCash: expectedAmount,
      totalRevenue: cashRegister?.summary?.totalRevenue ?? 0,
    };

    // Calculate from active orders
    if (ordersData && Array.isArray(ordersData)) {
      const openOrders = ordersData.filter(o => o.status === 'Open' || o.status === 'Preparing' || o.status === 'Ready' || o.status === 'Delivered');

      summary.openOrdersCount = openOrders.length;
      summary.openOrdersTotal = openOrders.reduce((sum, order) => sum + (order.total || 0), 0);

      // Calculate guests from open orders (current guests in restaurant)
      summary.currentGuests = openOrders.reduce((sum, order) => {
        return sum + (order.adults || 0) + (order.children || 0);
      }, 0);
    }

    return summary;
  }, [cashRegister, ordersData, expectedAmount]);

  const moneyStyle: React.CSSProperties = {
    fontFamily: 'ui-monospace, Consolas, monospace',
    fontVariantNumeric: 'tabular-nums',
    fontWeight: 600,
  };
  const maxSalesMethod = Math.max(
    daySummary.cashSales,
    daySummary.cardSales,
    daySummary.transferSales,
    daySummary.otherSales,
    1
  );

  const handlePrintTicket = async () => {
    if (!cashRegister) return;

    setPrinting(true);
    try {
      const summaryText = buildSummaryText();

      // Try Cloud Print first (if enabled)
      if (cloudPrintEnabled) {
        // Field names must match the backend EscPosReceiptData DTO exactly
        // (JSON binding is case-insensitive but does not translate names).
        // GenerateCashCountReceipt only reads RestaurantName/WaiterName/DateTime/Footer.
        await cloudPrintJob({
          printType: 'CashCount',
          receiptData: {
            restaurantName: restaurantSettings?.name || 'MASSRESTAURANT',
            title: 'ARQUEO DE CAJA',
            tableName: 'CAJA',
            waiterName: cashRegister.userName || '',
            total: expectedAmount,
            footer: `${summaryText}\n------------------------------------------\nMonto Esperado: ${currencySymbol}${expectedAmount.toFixed(2)}\nMonto Contado: ${currencySymbol}${expectedAmount.toFixed(2)}\nDiferencia: ${currencySymbol}0.00`,
          },
        });
        message.success('Arqueo impreso exitosamente');
      }
      // Try Print Agent (Windows)
      else if (printAgentAvailable) {
        const result = await printAgentService.printCashCount(
          restaurantSettings?.name || 'MASSRESTAURANT',
          cashRegister.userName || '',
          expectedAmount,
          expectedAmount, // For summary, we show expected as actual
          0, // No variance in summary mode
          summaryText
        );

        if (result.success) {
          message.success('Arqueo impreso exitosamente');
        } else {
          message.error(`Error al imprimir: ${result.message}`);
        }
      }
      // Try Bluetooth if Print Agent not available (Android/Chrome)
      else if (bluetoothAvailable) {
        const result = await bluetoothPrintService.printCashCount(
          restaurantSettings?.name || 'MASSRESTAURANT',
          cashRegister.userName || '',
          expectedAmount,
          expectedAmount,
          0,
          summaryText
        );

        if (result.success) {
          message.success('Arqueo impreso exitosamente');
        } else {
          message.error(`Error al imprimir: ${result.message}`);
        }
      }
      else {
        message.warning('No hay servicio de impresion disponible');
      }
    } catch (error: any) {
      console.error('Print error:', error);
      message.error('Error al imprimir el arqueo');
    } finally {
      setPrinting(false);
    }
  };

  const buildSummaryText = () => {
    const now = dayjs();
    let text = `RESUMEN DEL DIA\n`;
    text += `Fecha: ${now.format('DD/MM/YYYY HH:mm')}\n\n`;

    text += `VENTAS POR FORMA DE PAGO:\n`;
    text += `  Efectivo: ${currencySymbol}${daySummary.cashSales.toFixed(2)}\n`;
    text += `  Tarjeta: ${currencySymbol}${daySummary.cardSales.toFixed(2)}\n`;
    text += `  Transferencia: ${currencySymbol}${daySummary.transferSales.toFixed(2)}\n`;
    text += `  Otros: ${currencySymbol}${daySummary.otherSales.toFixed(2)}\n`;
    text += `  TOTAL VENTAS: ${currencySymbol}${daySummary.totalSales.toFixed(2)}\n\n`;

    text += `OTROS INGRESOS:\n`;
    text += `  Propinas: ${currencySymbol}${daySummary.tips.toFixed(2)}\n`;
    text += `  Correcciones: ${currencySymbol}${daySummary.corrections.toFixed(2)}\n\n`;

    text += `SALIDAS:\n`;
    text += `  Gastos: ${currencySymbol}${daySummary.expenses.toFixed(2)}\n`;
    text += `  Retiros: ${currencySymbol}${daySummary.drops.toFixed(2)}\n`;
    text += `  Pagos: ${currencySymbol}${daySummary.payouts.toFixed(2)}\n`;
    text += `  Descuentos: ${currencySymbol}${daySummary.totalDiscounts.toFixed(2)}\n\n`;

    text += `ORDENES EN MESA:\n`;
    text += `  Cantidad: ${daySummary.openOrdersCount}\n`;
    text += `  Total pendiente: ${currencySymbol}${daySummary.openOrdersTotal.toFixed(2)}\n\n`;

    text += `PERSONAS:\n`;
    text += `  Adultos: ${daySummary.totalAdults}\n`;
    text += `  Ninos: ${daySummary.totalChildren}\n`;
    text += `  Total del dia: ${daySummary.totalGuests}\n`;
    text += `  Actualmente en mesa: ${daySummary.currentGuests}\n\n`;

    text += `------------------------------------------\n`;
    text += `Monto Inicial: ${currencySymbol}${daySummary.initialAmount.toFixed(2)}\n`;
    text += `Efectivo Esperado: ${currencySymbol}${daySummary.expectedCash.toFixed(2)}\n`;
    text += `Ingresos Totales: ${currencySymbol}${daySummary.totalRevenue.toFixed(2)}\n`;

    return text;
  };

  const getOperatingTime = () => {
    if (!cashRegister) return '';
    const openTime = dayjs(cashRegister.openTime);
    const now = dayjs();
    const duration = now.diff(openTime, 'minute');

    const hours = Math.floor(duration / 60);
    const minutes = duration % 60;

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  return (
    <>
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap', paddingRight: 28 }}>
          <span>Arqueo de caja</span>
          <Text type="secondary" style={{ fontSize: 12, fontWeight: 400 }}>
            {cashRegister?.userName || 'Usuario'}
            {cashRegister?.openTime ? ` · abierta ${formatInTimeZone(cashRegister.openTime, 'HH:mm')}` : ''}
            {` · operando ${getOperatingTime()}`}
          </Text>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={isMobile ? '95%' : 720}
      style={{ top: isMobile ? 10 : 20 }}
      footer={
        <div style={{
          display: 'flex',
          flexDirection: isMobile ? 'column' : 'row',
          gap: 8,
          width: '100%',
        }}>
          <Button
            key="print"
            icon={<PrinterOutlined />}
            onClick={handlePrintTicket}
            loading={printing}
            disabled={!cashRegister}
            style={{ flex: isMobile ? undefined : 1 }}
            block={isMobile}
          >
            Imprimir Resumen
          </Button>
          <div style={{
            display: 'flex',
            gap: 8,
            flex: isMobile ? undefined : 1,
            justifyContent: 'flex-end',
          }}>
            <Button
              key="cancel"
              onClick={onCancel}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              Cerrar
            </Button>
            <Button
              key="submit"
              type="primary"
              onClick={onConfirm}
              style={{ flex: isMobile ? 1 : undefined }}
            >
              Confirmar Arqueo
            </Button>
          </div>
        </div>
      }
    >
      {ordersLoading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>
          <Spin size="large" />
          <Text style={{ display: 'block', marginTop: 16 }}>Cargando resumen...</Text>
        </div>
      ) : (
        <div>
          {/* Hero: el dato del arqueo primero */}
          <div
            style={{
              display: 'flex',
              gap: isMobile ? 14 : 26,
              flexWrap: 'wrap',
              alignItems: 'center',
              border: '1px solid var(--border-color)',
              borderRadius: 9,
              background: 'var(--bg-secondary)',
              padding: '10px 14px',
              marginBottom: 14,
            }}
          >
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Efectivo esperado</Text>
              <span style={{ ...moneyStyle, fontSize: 22, color: 'var(--color-primary)' }}>
                {currencySymbol}{daySummary.expectedCash.toFixed(2)}
              </span>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Ingresos totales</Text>
              <span style={{ ...moneyStyle, fontSize: 16 }}>{currencySymbol}{daySummary.totalRevenue.toFixed(2)}</span>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Movimientos</Text>
              <span style={{ ...moneyStyle, fontSize: 16 }}>{cashRegister?.summary?.totalTransactions ?? 0}</span>
            </div>
            <div>
              <Text type="secondary" style={{ fontSize: 11, display: 'block' }}>Propinas</Text>
              <span style={{ ...moneyStyle, fontSize: 16 }}>{currencySymbol}{daySummary.tips.toFixed(2)}</span>
            </div>
          </div>

          {/* Ventas por método como barras de proporción */}
          <div style={{ marginBottom: 14 }}>
            <Text strong style={{ fontSize: 13 }}>
              Ventas por método · <span style={moneyStyle}>{currencySymbol}{daySummary.totalSales.toFixed(2)}</span>
            </Text>
            {[
              { label: 'Efectivo', value: daySummary.cashSales },
              { label: 'Tarjeta', value: daySummary.cardSales },
              { label: 'Transferencia', value: daySummary.transferSales },
              { label: 'Otros', value: daySummary.otherSales },
            ].map((m) => (
              <div
                key={m.label}
                style={{
                  display: 'grid',
                  gridTemplateColumns: isMobile ? '80px 1fr 90px' : '110px 1fr 110px',
                  gap: 10,
                  alignItems: 'center',
                  padding: '4px 0',
                  fontSize: 13,
                }}
              >
                <span>{m.label}</span>
                <span style={{ height: 7, borderRadius: 4, background: 'var(--bg-tertiary)', overflow: 'hidden' }}>
                  <i
                    style={{
                      display: 'block',
                      height: '100%',
                      borderRadius: 4,
                      background: 'var(--color-primary)',
                      width: `${(m.value / maxSalesMethod) * 100}%`,
                    }}
                  />
                </span>
                <span style={{ ...moneyStyle, textAlign: 'right' }}>{currencySymbol}{m.value.toFixed(2)}</span>
              </div>
            ))}
          </div>

          {/* La aritmética que produce el esperado + lo que pasa en el piso */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr',
              gap: isMobile ? 14 : 24,
            }}
          >
            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 2 }}>Efectivo en caja</Text>
              {[
                { label: 'Fondo inicial', value: daySummary.initialAmount, sign: '' },
                { label: 'Ventas en efectivo', value: daySummary.cashSales, sign: '+' },
                { label: 'Correcciones', value: daySummary.corrections, sign: '+' },
                { label: 'Gastos', value: daySummary.expenses, sign: '−' },
                { label: 'Retiros', value: daySummary.drops, sign: '−' },
                { label: 'Pagos', value: daySummary.payouts, sign: '−' },
              ].map((line) => (
                <div key={line.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 13 }}>
                  <Text type="secondary">{line.label}</Text>
                  <span
                    style={{
                      ...moneyStyle,
                      color: line.sign === '+' ? 'var(--color-success-text)' : line.sign === '−' ? 'var(--color-error-text)' : 'var(--text-primary)',
                    }}
                  >
                    {line.sign}{currencySymbol}{line.value.toFixed(2)}
                  </span>
                </div>
              ))}
              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  padding: '4px 0',
                  fontSize: 13,
                  fontWeight: 650,
                  borderTop: '1px solid var(--border-color)',
                }}
              >
                <span>Esperado</span>
                <span style={moneyStyle}>{currencySymbol}{daySummary.expectedCash.toFixed(2)}</span>
              </div>
            </div>

            <div>
              <Text strong style={{ fontSize: 13, display: 'block', marginBottom: 2 }}>Piso ahora</Text>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 13 }}>
                <Text type="secondary">Órdenes abiertas</Text>
                <span style={moneyStyle}>
                  {daySummary.openOrdersCount} · {currencySymbol}{daySummary.openOrdersTotal.toFixed(2)}
                </span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 13 }}>
                <Text type="secondary">Personas en mesa</Text>
                <span style={moneyStyle}>{daySummary.currentGuests}</span>
              </div>
              <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 13 }}>
                <Text type="secondary">Comensales del día</Text>
                <span style={moneyStyle}>
                  {daySummary.totalGuests}{' '}
                  <Text type="secondary" style={{ fontWeight: 400 }}>
                    ({daySummary.totalAdults} ad · {daySummary.totalChildren} ni)
                  </Text>
                </span>
              </div>
              {daySummary.totalDiscounts > 0 && (
                <div style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 0', fontSize: 13 }}>
                  <Text type="secondary">Descuentos del día</Text>
                  <span style={{ ...moneyStyle, color: 'var(--color-error-text)' }}>
                    −{currencySymbol}{daySummary.totalDiscounts.toFixed(2)}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </Modal>
  </>
  );
};

export default CashCountModal;
